<template>
  <div class="flex min-h-screen bg-gray-50 dark:bg-gray-900">
    <!-- Sidebar -->
    <HrSidebar :isOpen="isSidebarOpen" />

    <main class="flex-1 transition-all duration-300" :class="{ 'ml-64': isSidebarOpen, 'ml-0': !isSidebarOpen }">
      <!-- Topbar -->
      <header class="fixed top-0 w-full z-50 bg-white dark:bg-gray-800 shadow">
        <HrTopbar
          :page-title="currentPageTitle"
          :is-sidebar-open="isSidebarOpen"
          :is-dark-mode="isDarkMode"
          @toggle-sidebar="toggleSidebar"
          @toggle-dark-mode="toggleDarkMode"
        />
      </header>

      <div class="p-6 mt-20">
        <!-- Search + Export -->
        <div class="flex justify-end items-center mb-8 gap-2">
          <button 
            type="submit" 
            class="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700 transition"
            @click="showFilter = !showFilter"
    
             >
            {{ showFilter ? 'Hide Filter' : 'Show Filter' }}
            
             </button>
          <button
            @click="exportLogs"
            class="bg-[#74bd54] text-white px-6 py-3 rounded-lg hover:bg-[#63a43f] flex items-center gap-2"
          >
            <span>↓ Export CSV</span>
          </button>
        </div>

        <!--Filter-->
        <Transition name="filter">
            <div v-if="showFilter">
                <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4 mb-6">
                  <input v-model="search" placeholder="Search user or action..." class="border rounded-lg px-4 py-3 border-gray-300 focus:border-secondary focus:outline-none" />
                
                  <select v-model="selectedAction" class="border rounded-lg px-4 py-3 border-gray-300 focus:border-secondary focus:outline-none"">
                    <option value="">All Actions</option>
                    <option value="LOGIN_SUCCESS">Login Success</option>
                    <option value="LOGIN_FAILED">Login Failed</option>
                    <!-- Add more actions as needed -->
                  </select>
              
                  <input v-model="fromDate" type="date" class="border rounded-lg px-4 py-3 border-gray-300 focus:border-secondary focus:outline-none" />
                  <input v-model="toDate" type="date" class="border rounded-lg px-4 py-3 border-gray-300 focus:border-secondary focus:outline-none" />
              
                  <button @click="fetchAuditLogs(1)" class="hover:bg-secondary hover:text-white px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 text-gray-600 dark:text-gray-300">
                    Apply Filters
                  </button>
                </div>
            </div>
        </Transition>
        <!-- Audit Logs Table -->
        <div class="bg-white dark:bg-gray-800 rounded-xl shadow overflow-hidden">
          <table class="w-full">
            <thead class="bg-gray-100 dark:bg-gray-700">
              <tr>
                <th class="px-6 py-4 text-left text-sm font-semibold text-gray-600 dark:text-gray-300">ID</th>
                <th class="px-6 py-4 text-left text-sm font-semibold text-gray-600 dark:text-gray-300">Full Name</th>
                <th class="px-6 py-4 text-left text-sm font-semibold text-gray-600 dark:text-gray-300">Action</th>
                <th class="px-6 py-4 text-left text-sm font-semibold text-gray-600 dark:text-gray-300">Description</th>
                <th class="px-6 py-4 text-left text-sm font-semibold text-gray-600 dark:text-gray-300">Entity</th>
                <th class="px-6 py-4 text-left text-sm font-semibold text-gray-600 dark:text-gray-300">IP Address</th>
                <th class="px-6 py-4 text-left text-sm font-semibold text-gray-600 dark:text-gray-300">Created At</th>
                <!-- <th class="px-6 py-4 text-center text-sm font-semibold text-gray-600 dark:text-gray-300">Actions</th> -->
              </tr>
            </thead>
            <tbody class="divide-y divide-gray-200 dark:divide-gray-700">
              <tr
                v-for="log in filteredLogs"
                :key="log.id"
                class="hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
              >
                <td class="px-6 py-4 text-sm font-medium">{{ log.id }}</td>
                <td class="px-6 py-4 text-sm">{{ log.user_full_name || 'System' }}</td>
                <td class="px-6 py-4">
                  <span
                    :class="{
                      'px-3 py-1 rounded-full text-xs font-medium': true,
                      'bg-green-100 text-green-700 dark:bg-green-900 dark:text-green-300': log.action.includes('SUCCESS'),
                      'bg-red-100 text-red-700 dark:bg-red-900 dark:text-red-300': log.action.includes('FAILED')
                    }"
                  >
                    {{ log.action }}
                  </span>
                </td>
                <td class="px-6 py-4 text-sm text-gray-600 dark:text-gray-400">{{ log.description }}</td>
                <td class="px-6 py-4 text-sm">
                  <span v-if="log.entity_type" class="text-xs bg-gray-200 dark:bg-gray-600 px-2 py-1 rounded">
                    {{ log.entity_type }} #{{ log.entity_id }}
                  </span>
                </td>
                <td class="px-6 py-4 text-sm font-mono">{{ log.ip_address }}</td>
                <td class="px-6 py-4 text-sm text-gray-500">{{ formatDate(log.created_at) }}</td>
                <!-- <td class="px-6 py-4 text-center">
                  <div class="flex justify-center gap-4 text-sm">
                    <button @click="copyLog(log)" class="text-blue-600 hover:underline">Copy</button>
                    <button @click="editLog(log)" class="text-amber-600 hover:underline">Edit</button>
                    <button @click="confirmDelete(log.id)" class="text-red-600 hover:underline">Delete</button>
                  </div>
                </td> -->
              </tr>
            </tbody>
          </table>
          <!-- Pagination -->
          <div v-if="pagination.total_pages > 1" class="flex items-center justify-between mt-8 py-2 px-2">

            <div class="text-sm text-gray-500 dark:text-gray-400">
              Showing 
              <span class="font-medium text-gray-700 dark:text-gray-300">
                {{ (pagination.current_page - 1) * pagination.per_page + 1 }}
              </span> 
              to 
              <span class="font-medium text-gray-700 dark:text-gray-300">
                {{ Math.min(pagination.current_page * pagination.per_page, pagination.total) }}
              </span> 
              of 
              <span class="font-medium text-gray-700 dark:text-gray-300">{{ pagination.total }}</span> 
              entries
            </div>
          
            <div class="flex items-center gap-2">
              <!-- Previous Button -->
              <button
                @click="changePage(pagination.current_page - 1)"
                :disabled="pagination.current_page === 1"
                class="px-4 py-2 border rounded-lg flex items-center gap-1 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                ← Previous
              </button>
            
              <!-- Page Numbers -->
              <div class="flex gap-1">
                <button
                  v-for="page in visiblePages"
                  :key="page"
                  @click="changePage(page)"
                  :class="[
                    'w-10 h-10 flex items-center justify-center rounded-lg border text-sm',
                    pagination.current_page === page 
                      ? 'bg-[#74bd54] text-white border-[#74bd54]' 
                      : 'hover:bg-gray-100 dark:hover:bg-gray-700'
                  ]"
                >
                  {{ page }}
                </button>
              </div>
            
              <!-- Next Button -->
              <button
                @click="changePage(pagination.current_page + 1)"
                :disabled="pagination.current_page === pagination.total_pages"
                class="px-4 py-2 border rounded-lg flex items-center gap-1 hover:bg-gray-100 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next →
              </button>
            </div>
          </div>
        </div>

        <div v-if="filteredLogs.length === 0" class="text-center text-gray-400 py-20">
          No audit logs found
        </div>

        <!-- Confirm Delete Modal -->
        <ConfirmModal
          :show="showDeleteModal"
          title="Delete Log Entry"
          message="Are you sure you want to delete this audit log? This action cannot be undone."
          confirmText="Delete"
          cancelText="Cancel"
          @confirm="handleDelete"
          @cancel="showDeleteModal = false"
        />
      </div>
    </main>
  </div>
</template>

<script setup>
import { ref, computed, onMounted, watch } from "vue";
import HrSidebar from "@/components/hr/HrSidebar.vue";
import HrTopbar from "@/components/hr/HrTopbar.vue";
import ConfirmModal from "@/components/hr/ConfirmModal.vue";
import { useToast } from 'vue-toastification';
import api from "@/services/api";   

const toast = useToast();

const currentPageTitle = ref("Audit Logs");
const isSidebarOpen = ref(true);
const isDarkMode = ref(false);
const selectedAction = ref("");
const search = ref("");
const logs = ref([]);         
const showDeleteModal = ref(false);
const itemToDelete = ref(null);
const fromDate = ref("");     
const toDate = ref("");
const showFilter = ref(false);


const pagination = ref({
  current_page: 1,
  per_page: 20,
  total: 0,
  total_pages: 1
});

const loading = ref(false);

// Fetch Logs
const fetchAuditLogs = async (page = 1) => {
  loading.value = true;
  try {
    const params = {
      page: page,
      limit: pagination.value.per_page,
      search: search.value,
      action: selectedAction.value,
      from_date: fromDate.value,
      to_date: toDate.value,
      user_type: ["HR", "Human Resources"]
    };

    const response = await api.get("AuditLogs.php", { params });

    logs.value = response.data.data || [];
    pagination.value = response.data.pagination;
  } catch (error) {
    console.error(error);
    logs.value = [];
    toast.error("Failed to load audit logs");
  } finally {
    loading.value = false;
  }
};

// Watch filters and reset to page 1 when changed
watch([search, selectedAction, fromDate, toDate], () => {
  pagination.value.current_page = 1;
  fetchAuditLogs(1);
});

const changePage = (page) => {
  if (page < 1 || page > pagination.value.total_pages) return;
  pagination.value.current_page = page;
  fetchAuditLogs(page);
};

const filteredLogs = computed(() => {
  if (!Array.isArray(logs.value)) return [];

  return logs.value
    .filter(log =>
      ["hr", "human resources"].includes(
        log.user_type?.toString().toLowerCase()
      )
    )
    .filter(log =>
      Object.values(log).some(value =>
        value?.toString().toLowerCase().includes(search.value.toLowerCase())
      )
    );
});

const formatDate = (dateString) => {
  if (!dateString) return "—";
  return new Date(dateString).toLocaleString('en-US', {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit'
  });
};

// ... rest of your functions (copyLog, editLog, confirmDelete, etc.)

const copyLog = (log) => {
  const text = `ID: ${log.id}\nAction: ${log.action}\nDescription: ${log.description}\nIP: ${log.ip_address}\nTime: ${log.created_at}`;
  navigator.clipboard.writeText(text).then(() => {
    toast.success("Log copied to clipboard");
  });
};

const editLog = (log) => {
  toast.info(`Editing log #${log.id} - Coming soon`);
};

const confirmDelete = (id) => {
  itemToDelete.value = id;
  showDeleteModal.value = true;
};

const handleDelete = async () => {
  try {
    await api.delete("AuditLogs.php", { data: { id: itemToDelete.value } });
    logs.value = logs.value.filter(log => log.id !== itemToDelete.value);
    toast.success("Log entry deleted successfully");
  } catch (err) {
    toast.error("Failed to delete log");
  } finally {
    showDeleteModal.value = false;
    itemToDelete.value = null;
  }
};

const exportLogs = () => {
  if (logs.value.length === 0) {
    toast.warning("No logs to export");
    return;
  }

  let csv = "ID,User ID,Action,Description,Entity Type,Entity ID,IP Address,User Agent,Created At\n";
  
  logs.value.forEach(log => {
    csv += `"${log.id}","${log.user_id || ''}","${log.action}","${(log.description || '').replace(/"/g, '""')}","${log.entity_type || ''}","${log.entity_id || ''}","${log.ip_address || ''}","${(log.user_agent || '').replace(/"/g, '""')}","${log.created_at || ''}"\n`;
  });

  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `audit-logs-${new Date().toISOString().slice(0,10)}.csv`;
  link.click();
  toast.success("Logs exported successfully");
};

const toggleSidebar = () => { isSidebarOpen.value = !isSidebarOpen.value; };

const toggleDarkMode = () => {
  isDarkMode.value = !isDarkMode.value;
  if (isDarkMode.value) {
    document.documentElement.classList.add("dark");
  } else {
    document.documentElement.classList.remove("dark");
  }
};

onMounted(() => {
  fetchAuditLogs();
});
</script>

  <style>
filter-enter-active,
.filter-leave-active {
  transition: all 0.35s ease;
}

.filter-enter-from,
.filter-leave-to {
  opacity: 0;
  transform: translateY(-15px);
  max-height: 0;
}

.filter-enter-to,
.filter-leave-from {
  opacity: 1;
  transform: translateY(0);
  max-height: 1000px;
}

</style>